import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import VideoCard from '@/components/feature/VideoCard';
import VideoPlayer from '@/pages/detail/components/VideoPlayer';
import { categoryMap } from '@/mocks/categories';
import { getRelatedVideos, getVideoById, formatPlays } from '@/utils/videoUtils';
import { useFavorites } from '@/hooks/useFavorites';
import { usePageMeta } from '@/hooks/usePageMeta';

export default function Detail() {
  const { id = '' } = useParams();
  const video = getVideoById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState('');

  usePageMeta({
    title: video ? `${video.title} - 在线视频` : '视频详情 - 在线视频',
    description: video
      ? `${video.description.slice(0, 80)}…在线视频提供《${video.title}》的在线观看与相关推荐。`
      : '在线视频详情页。',
    keywords: video ? `${video.title},${video.type},在线观看,在线视频` : '在线视频详情',
  });

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(''), 2200);
    return () => window.clearTimeout(t);
  }, [toast]);

  if (!video) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-display text-2xl font-bold text-foreground-950">视频不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">你访问的视频可能已下架或链接有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const category = categoryMap[video.category];
  const related = getRelatedVideos(video, 4);
  const favorited = isFavorite(video.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast('链接已复制，快去分享给朋友吧！');
    } catch {
      setToast('复制失败，请手动复制地址栏链接。');
    }
  };

  const handleFavorite = () => {
    const now = toggleFavorite(video.id);
    setToast(now ? '已加入收藏，可在本地随时查看。' : '已取消收藏。');
  };

  const metaList = [
    { icon: 'ri-price-tag-3-line', label: '类型', value: video.type },
    { icon: 'ri-calendar-line', label: '上映年份', value: `${video.year}` },
    { icon: 'ri-map-pin-line', label: '地区', value: video.region },
    { icon: 'ri-time-line', label: '时长', value: video.duration },
    { icon: 'ri-history-line', label: '更新日期', value: video.updatedAt },
  ];

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 pt-6">
        <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to={`/category/${video.category}`} className="hover:text-primary-700 transition-colors">
            {category?.name}
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900 font-medium">{video.title}</span>
        </nav>
      </div>

      <div className="w-full px-4 md:px-6 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <VideoPlayer
              cover={video.cover}
              title={video.title}
              duration={video.duration}
              rating={video.rating}
            />

            <div className="mt-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0">
                <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground-950">
                  {video.title}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-500">{video.subtitle}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    favorited
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-100 border border-background-300 text-foreground-800 hover:border-accent-400'
                  }`}
                >
                  <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-foreground-800 hover:border-primary-400 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line"></i>
                  分享
                </button>
              </div>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-lg bg-background-100 border border-background-200 px-4 md:px-5 py-3.5">
              {metaList.map((m) => (
                <div key={m.label} className="flex items-center gap-2 text-sm">
                  <i className={`${m.icon} text-primary-600`}></i>
                  <span className="text-foreground-500">{m.label}</span>
                  <span className="text-foreground-900 font-medium">{m.value}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 text-sm">
                <i className="ri-fire-line text-accent-500"></i>
                <span className="text-foreground-500">播放量</span>
                <span className="text-foreground-900 font-medium">{formatPlays(video.plays)}</span>
              </div>
            </div>

            <section className="mt-6">
              <h2 className="font-display text-lg font-bold text-foreground-950 mb-3">剧情简介</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {video.description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </section>

            <div className="mt-6">
              <Link
                to={`/category/${video.category}`}
                className="inline-flex items-center gap-2 text-sm text-primary-700 hover:text-primary-800 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回{category?.name}分类
              </Link>
            </div>
          </div>

          <aside className="w-full lg:w-80 shrink-0">
            <div className="rounded-lg bg-background-50 border border-background-200 p-4 lg:sticky lg:top-20">
              <h2 className="font-display text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                相关推荐
              </h2>
              <div className="space-y-3">
                {related.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="group flex gap-3 rounded-md p-2 -m-2 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <div className="w-28 shrink-0 aspect-video rounded-md overflow-hidden bg-background-200 relative">
                      <img
                        src={item.cover}
                        alt={`${item.title}封面`}
                        title={`${item.title} - 在线观看`}
                        className="w-full h-full object-cover object-top"
                      />
                      <span className="absolute bottom-1 right-1 px-1.5 py-0.5 rounded bg-foreground-950/75 text-background-100 text-[10px]">
                        {item.duration}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-700 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500 line-clamp-2">{item.subtitle}</p>
                      <span className="mt-1.5 inline-flex items-center gap-1 text-xs text-accent-600">
                        <i className="ri-star-fill"></i>
                        {item.rating.toFixed(1)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="font-display text-lg md:text-xl font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <span className="w-1.5 h-5 rounded-full bg-primary-500"></span>
              更多人还在看
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {related.map((item) => (
                <VideoCard key={`grid-${item.id}`} video={item} />
              ))}
            </div>
          </section>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[70] animate-float-up">
          <div className="flex items-center gap-2 px-5 py-3 rounded-lg bg-foreground-950 text-background-50 text-sm shadow-lg">
            <i className="ri-checkbox-circle-fill text-accent-400"></i>
            {toast}
          </div>
        </div>
      )}
    </SiteLayout>
  );
}